import { useEffect, useRef, useState } from "react";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { App as CapacitorApp } from "@capacitor/app";
import { useAtom, useSetAtom } from "jotai";
import { pushPermissionState, pushTokenState } from "@/lib/store";
import { isAppVersionAtLeast, isFcmToken, isPushSupported, PUSH_CHANNEL_ID, routePushTap, toPushPermission } from "@/lib/push";
import { PushPlatform, registerPushDevice, reportPushOpen } from "@/controllers/push";

let appVersionPromise: Promise<string> | null = null;
export const getAppVersion = () => {
    if (!appVersionPromise) {
        appVersionPromise = CapacitorApp.getInfo()
            .then((info) => info.version)
            .catch(() => "");
    }
    return appVersionPromise;
};

/**
 * True once we know this is an app build with the native push setup (see PUSH_MIN_APP_VERSION).
 * The pre-prompt is only offered on such builds.
 */
export function usePushNativeReady() {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        if (!isPushSupported()) return;
        let cancelled = false;
        getAppVersion().then((v) => !cancelled && setReady(isAppVersionAtLeast(v)));
        return () => {
            cancelled = true;
        };
    }, []);
    return ready;
}

/**
 * Asks the OS for notification permission (after our own pre-prompt) and registers
 * with FCM when granted. Returns the resulting permission.
 */
export const requestPushPermission = async () => {
    const result = await PushNotifications.requestPermissions();
    const permission = toPushPermission(result.receive);
    if (permission === "granted") await PushNotifications.register();
    return permission;
};

/**
 * Phone push notifications (ortex/ted/ted#2485). Native app only; a no-op on the web
 * and on app builds without the PushNotifications plugin.
 *
 * - Registers with FCM on every start once permission is granted, and POSTs the token
 *   to the backend; re-POSTs when the signed-in user changes so the backend links or
 *   detaches the device.
 * - Leaves "prompt" to the pre-prompt (PushPrePrompt), which reads pushPermissionState.
 * - Routes tapped notifications by their data payload.
 * - Foreground notifications: presentationOptions ("alert") makes both iOS and the Android
 *   plugin show the system notification while the app is open, so there is no in-app toast.
 *
 * `authKey` identifies the signed-in user ("anon" when signed out); undefined while
 * the user is still loading, so the first POST waits for it.
 */
export function usePushNotifications(push: (route: string) => void, authKey: string | undefined) {
    const [token, setToken] = useAtom(pushTokenState);
    const setPermission = useSetAtom(pushPermissionState);

    const pushRef = useRef(push);
    useEffect(() => {
        pushRef.current = push;
    }, [push]);
    // The listener is set up once; it reads the latest token from here.
    const tokenRef = useRef(token);
    useEffect(() => {
        tokenRef.current = token;
    }, [token]);

    useEffect(() => {
        if (!isPushSupported()) return;

        const handles: Promise<PluginListenerHandle>[] = [];

        handles.push(PushNotifications.addListener("registration", ({ value }) => setToken(value)));
        handles.push(PushNotifications.addListener("registrationError", (err) => console.warn("Push registration failed", err?.error)));
        handles.push(
            PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
                const data = notification?.data;
                if (data?.type) reportPushOpen(String(data.type), data.pushId ? String(data.pushId) : undefined, tokenRef.current);
                routePushTap(data, pushRef.current);
            })
        );
        if (Capacitor.getPlatform() === "android") {
            PushNotifications.createChannel({ id: PUSH_CHANNEL_ID, name: "ORTEX", description: "Alerts, news and offers from ORTEX", importance: 4, visibility: 1 }).catch(() => undefined);
        }

        let registered = false;
        const sync = async () => {
            try {
                const { receive } = await PushNotifications.checkPermissions();
                const permission = toPushPermission(receive);
                setPermission(permission);
                if (permission === "granted" && !registered) {
                    registered = true;
                    await PushNotifications.register();
                }
            } catch (e) {
                console.warn("Push permission check failed", e);
            }
        };
        sync();

        // The user may turn notifications on in the phone's settings and come back.
        handles.push(CapacitorApp.addListener("appStateChange", ({ isActive }) => isActive && sync()));

        return () => {
            handles.forEach((h) => h.then((handle) => handle.remove()).catch(() => undefined));
        };
    }, [setPermission, setToken]);

    // Register the token with the backend once the user is known, and again whenever the
    // signed-in user changes (sign-in links the device, sign-out detaches it).
    const postedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!token || authKey === undefined || !isFcmToken(token)) return;
        const key = `${token}|${authKey}`;
        if (postedRef.current === key) return;
        postedRef.current = key;
        const platform = Capacitor.getPlatform() as PushPlatform;
        getAppVersion().then((appVersion) => registerPushDevice(token, platform, appVersion));
    }, [token, authKey]);
}
